import { apiClient } from '@/services/api-client';
import {
  CreateCustomerTeamInput,
  CreateCustomerTeamResult,
  CustomerTeamInvitation,
  CustomerTeamMember,
  CustomerTeamRole,
  CustomerTeamStats,
  CustomerTeamSummary,
  InviteTeamMemberInput,
  UpdateTeamMemberRoleInput,
} from '@/types/customer-teams';

interface JsonApiResource<TAttributes = Record<string, unknown>> {
  id: string;
  type: string;
  attributes?: TAttributes;
  relationships?: Record<string, { data?: { id: string; type: string } | Array<{ id: string; type: string }>; meta?: Record<string, unknown> }>;
  meta?: Record<string, unknown>;
}

interface JsonApiResponse<T = unknown> {
  data: T;
  included?: JsonApiResource[];
  meta?: Record<string, unknown>;
}

const ROLE_LABEL_FROM_META: Record<string, CustomerTeamRole> = {
  owner: 'owner',
  admin: 'admin',
  member: 'member',
};

const STATUS_LABEL_FROM_META: Record<string, CustomerTeamMember['status']> = {
  active: 'active',
  invited: 'invited',
  pending: 'pending',
  suspended: 'suspended',
  inactive: 'inactive',
};

const DEFAULT_ROLE_LABEL: Record<CustomerTeamRole, string> = {
  owner: 'Owner',
  admin: 'Admin',
  member: 'Member',
};

function toOptionalString(value: unknown): string | undefined {
  if (typeof value !== 'string') {
    return undefined;
  }
  const trimmed = value.trim();
  return trimmed.length ? trimmed : undefined;
}

function toOptionalNumber(value: unknown): number | undefined {
  if (value === null || value === undefined) {
    return undefined;
  }

  if (typeof value === 'number' && Number.isFinite(value)) {
    return value;
  }

  if (typeof value === 'string' && value.trim().length) {
    const parsed = Number(value);
    if (!Number.isNaN(parsed)) {
      return parsed;
    }
  }

  return undefined;
}

function resolveCustomerName(attributes: Record<string, unknown>): string {
  const directName =
    toOptionalString(attributes.name) ??
    toOptionalString(attributes.full_name) ??
    toOptionalString(attributes.fullName) ??
    toOptionalString(attributes.display_name) ??
    toOptionalString(attributes.displayName);

  if (directName) {
    return directName;
  }

  const firstName =
    toOptionalString(attributes.first_name) ??
    toOptionalString(attributes.firstName) ??
    toOptionalString(attributes.given_name) ??
    toOptionalString(attributes.givenName);
  const lastName =
    toOptionalString(attributes.last_name) ??
    toOptionalString(attributes.lastName) ??
    toOptionalString(attributes.family_name) ??
    toOptionalString(attributes.familyName);

  const combined = [firstName, lastName].filter(Boolean).join(' ').trim();
  if (combined.length) {
    return combined;
  }

  const profileName = toOptionalString((attributes.profile as Record<string, unknown> | undefined)?.name);
  return profileName ?? 'Unknown member';
}

function resolveEmail(attributes: Record<string, unknown>): string {
  return (
    toOptionalString(attributes.email) ??
    toOptionalString(attributes.primary_email) ??
    toOptionalString(attributes.primaryEmail) ??
    toOptionalString(attributes.email_address) ??
    toOptionalString(attributes.contact_email) ??
    toOptionalString(attributes.work_email) ??
    ''
  );
}

function resolveAvatar(attributes: Record<string, unknown>): string | undefined {
  return (
    toOptionalString(attributes.avatar_url) ??
    toOptionalString(attributes.avatarUrl) ??
    toOptionalString(attributes.avatar) ??
    toOptionalString(attributes.profile_image_url) ??
    toOptionalString(attributes.profileImageUrl)
  );
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function resolveAttributes(resource: JsonApiResource): Record<string, unknown> {
  if (isRecord(resource.attributes)) {
    return resource.attributes as Record<string, unknown>;
  }

  if (!isRecord(resource)) {
    return {};
  }

  const candidate = resource as unknown as Record<string, unknown>;
  const result: Record<string, unknown> = {};
  const excludedKeys = new Set(['id', 'type', 'attributes', 'relationships', 'meta', 'links', '_meta', '_links']);

  Object.entries(candidate).forEach(([key, value]) => {
    if (!excludedKeys.has(key) && !key.endsWith('Meta') && !key.endsWith('Links')) {
      result[key] = value;
    }
  });

  return result;
}

type Relationship = NonNullable<JsonApiResource['relationships']>[string];
type RelationshipRef = { id: string; type?: string };

interface ResolvedRelationship {
  data?: unknown;
  meta?: Record<string, unknown>;
}

function resolveRelationship(resource: JsonApiResource, key: string): ResolvedRelationship | undefined {
  const relationships = resource.relationships ?? {};
  const direct = relationships[key] as Relationship | undefined;
  if (direct) {
    return direct;
  }

  const flatResource = resource as unknown as Record<string, unknown>;
  const flatData = flatResource[key];
  const flatMeta = flatResource[`${key}Meta`];

  if (flatData !== undefined || flatMeta !== undefined) {
    return {
      data: flatData,
      meta: isRecord(flatMeta) ? (flatMeta as Record<string, unknown>) : undefined,
    };
  }

  const resourceMeta = resource.meta;
  if (isRecord(resourceMeta) && isRecord(resourceMeta[key])) {
    return {
      data: flatData,
      meta: resourceMeta[key] as Record<string, unknown>,
    };
  }

  const legacyMeta = flatResource._meta;
  if (isRecord(legacyMeta) && isRecord(legacyMeta[key])) {
    return {
      data: flatData,
      meta: legacyMeta[key] as Record<string, unknown>,
    };
  }

  return undefined;
}

function normalizeRelationshipRefs(data: unknown): RelationshipRef[] {
  if (!data) {
    return [];
  }

  if (Array.isArray(data)) {
    const refs: RelationshipRef[] = [];
    data.forEach((item) => {
      if (item && typeof item === 'object' && typeof (item as { id?: unknown }).id === 'string') {
        refs.push({
          id: (item as { id: string }).id,
          type: typeof (item as { type?: unknown }).type === 'string' ? ((item as { type?: string }).type as string) : undefined,
        });
      } else if (typeof item === 'string') {
        refs.push({ id: item });
      }
    });
    return refs;
  }

  if (typeof data === 'string') {
    return [{ id: data }];
  }

  if (isRecord(data)) {
    if (typeof data.id === 'string') {
      return [{ id: data.id, type: typeof data.type === 'string' ? (data.type as string) : undefined }];
    }

    if (data.data !== undefined) {
      return normalizeRelationshipRefs(data.data);
    }
  }

  return [];
}

function mergeMemberDetails(primary: CustomerTeamMember, supplemental: CustomerTeamMember): CustomerTeamMember {
  return {
    ...primary,
    ...supplemental,
    name:
      supplemental.name && supplemental.name !== 'Unknown member'
        ? supplemental.name
        : primary.name,
    email: supplemental.email || primary.email,
    role: supplemental.role ?? primary.role,
    roleLabel: supplemental.roleLabel ?? primary.roleLabel,
    status: supplemental.status ?? primary.status,
    statusLabel: supplemental.statusLabel ?? primary.statusLabel,
    joinedAt: supplemental.joinedAt ?? primary.joinedAt,
    invitedAt: supplemental.invitedAt ?? primary.invitedAt,
    lastActive: supplemental.lastActive ?? primary.lastActive,
    avatarUrl: supplemental.avatarUrl ?? primary.avatarUrl,
    customerId: supplemental.customerId ?? primary.customerId,
  };
}

function parseMetaMember(entry: Record<string, unknown>, membershipId?: string): CustomerTeamMember | undefined {
  const memberDetails = isRecord(entry.member) ? (entry.member as Record<string, unknown>) : undefined;

  const resolvedMembershipId =
    toOptionalString(entry.membership_id) ??
    toOptionalString(entry.membershipId) ??
    toOptionalString(entry.id) ??
    toOptionalString(memberDetails?.membership_id) ??
    toOptionalString(memberDetails?.membershipId) ??
    membershipId;

  const derivedCustomerIdFromMembership =
    resolvedMembershipId && resolvedMembershipId.includes(':') ? resolvedMembershipId.split(':')[1] : undefined;

  const resolvedCustomerId =
    toOptionalString(entry.customer_id) ??
    toOptionalString(entry.customerId) ??
    toOptionalString(memberDetails?.customer_id) ??
    toOptionalString(memberDetails?.customerId) ??
    toOptionalString(memberDetails?.id) ??
    derivedCustomerIdFromMembership ??
    resolvedMembershipId;

  if (!resolvedCustomerId) {
    return undefined;
  }

  const roleKeyRaw =
    toOptionalString(entry.role) ??
    toOptionalString(entry.role_key) ??
    toOptionalString(memberDetails?.role) ??
    'member';
  const normalizedRoleKey = roleKeyRaw ? roleKeyRaw.toLowerCase() : 'member';
  const role = ROLE_LABEL_FROM_META[normalizedRoleKey] ?? 'member';

  const roleLabel =
    toOptionalString(entry.role_label) ??
    toOptionalString(entry.roleLabel) ??
    toOptionalString(memberDetails?.role_label) ??
    toOptionalString(memberDetails?.roleLabel) ??
    DEFAULT_ROLE_LABEL[role];

  const statusKeyRaw =
    toOptionalString(entry.status) ??
    toOptionalString(entry.status_key) ??
    toOptionalString(memberDetails?.status) ??
    'active';
  const normalizedStatusKey = statusKeyRaw ? statusKeyRaw.toLowerCase() : 'active';
  const status = STATUS_LABEL_FROM_META[normalizedStatusKey] ?? 'active';

  const statusLabel =
    toOptionalString(entry.status_label) ??
    toOptionalString(entry.statusLabel) ??
    toOptionalString(memberDetails?.status_label) ??
    toOptionalString(memberDetails?.statusLabel) ??
    (status === 'active' ? 'Active' : status);

  const joinedAt =
    toOptionalString(entry.joined_at) ??
    toOptionalString(entry.joinedAt) ??
    toOptionalString(memberDetails?.joined_at) ??
    toOptionalString(memberDetails?.joinedAt) ??
    new Date().toISOString();

  const invitedAt =
    toOptionalString(entry.invited_at) ??
    toOptionalString(entry.invitedAt) ??
    toOptionalString(memberDetails?.invited_at) ??
    toOptionalString(memberDetails?.invitedAt);

  const lastActive =
    toOptionalString(entry.last_active) ??
    toOptionalString(entry.lastActive) ??
    toOptionalString(memberDetails?.last_active) ??
    toOptionalString(memberDetails?.lastActive);

  let email = memberDetails ? resolveEmail(memberDetails) : '';
  if (!email) {
    email =
      toOptionalString(entry.member_email) ??
      toOptionalString(entry.email) ??
      toOptionalString(memberDetails?.email) ??
      '';
  }

  let name = memberDetails ? resolveCustomerName(memberDetails) : 'Unknown member';
  if (name === 'Unknown member') {
    name =
      toOptionalString(entry.member_name) ??
      toOptionalString(entry.memberName) ??
      (email ? email.split('@')[0] : 'Unknown member');
  }

  const avatarUrl = memberDetails ? resolveAvatar(memberDetails) : undefined;

  return {
    id: resolvedMembershipId ?? resolvedCustomerId,
    customerId: resolvedCustomerId,
    name,
    email,
    role,
    roleLabel,
    status,
    statusLabel,
    joinedAt,
    invitedAt,
    lastActive,
    avatarUrl,
  };
}

function readMembersFromMeta(meta: unknown, membershipRefs: RelationshipRef[]): CustomerTeamMember[] {
  if (!isRecord(meta)) {
    return [];
  }

  const entries: Record<string, unknown>[] = [];
  const membersBlock = meta.members;

  if (Array.isArray(membersBlock) && membersBlock.length) {
    membersBlock.forEach((item) => {
      if (isRecord(item)) {
        entries.push(item);
      }
    });
  } else {
    const fallback = { ...meta };
    delete fallback.members;
    entries.push(fallback);
  }

  return entries
    .map((entry, index) => parseMetaMember(entry, membershipRefs[index]?.id ?? membershipRefs[0]?.id))
    .filter((member): member is CustomerTeamMember => Boolean(member));
}

function extractTeamMembers(resource: JsonApiResource, response: JsonApiResponse): CustomerTeamMember[] {
  const membershipRelationship = resolveRelationship(resource, 'membership');

  if (!membershipRelationship) {
    return [];
  }

  const membershipRefs = normalizeRelationshipRefs(membershipRelationship.data);

  const metaMembers = readMembersFromMeta(membershipRelationship.meta, membershipRefs);

  const includedMembers = membershipRefs
    .map((ref) => findIncluded(response, ref.type ?? 'team_memberships', ref.id))
    .filter((resourceEntry): resourceEntry is JsonApiResource => Boolean(resourceEntry))
    .map((membershipResource) => readMember(membershipResource, response));

  if (!metaMembers.length && !includedMembers.length) {
    return [];
  }

  const membersByKey = new Map<string, CustomerTeamMember>();

  [...metaMembers, ...includedMembers].forEach((member) => {
    const key = member.id || member.customerId;
    if (!key) {
      return;
    }
    const existing = membersByKey.get(key);
    membersByKey.set(key, existing ? mergeMemberDetails(existing, member) : member);
  });

  return Array.from(membersByKey.values());
}

function readTeamSummary(resource: JsonApiResource, response: JsonApiResponse): CustomerTeamSummary {
  const attributes = resolveAttributes(resource);
  const membershipRelationship = resolveRelationship(resource, 'membership');
  const membershipMetaRaw = membershipRelationship?.meta ?? {};
  const membershipMeta = isRecord(membershipMetaRaw) ? membershipMetaRaw : {};
  const membershipsFromMetaArray = Array.isArray((membershipMeta as { members?: unknown }).members);

  const roleKeyRaw =
    toOptionalString(membershipMeta.role) ??
    toOptionalString(membershipMeta.roleKey);
  const normalizedRoleKey = roleKeyRaw ? roleKeyRaw.toLowerCase() : undefined;
  const normalizedRole = normalizedRoleKey && normalizedRoleKey in ROLE_LABEL_FROM_META ? ROLE_LABEL_FROM_META[normalizedRoleKey] : 'member';

  const memberships = extractTeamMembers(resource, response);
  const rawMembersCount = Number(attributes.members ?? attributes.member_count ?? attributes.memberCount ?? 0);
  const membersCount = memberships.length ? memberships.length : rawMembersCount;

  return {
    id: resource.id,
    name: String(attributes.name ?? attributes.team_name ?? ''),
    description: typeof attributes.description === 'string' ? attributes.description : undefined,
    totalSeats: Number(attributes.max_seats ?? attributes.maxSeats ?? attributes.total_seats ?? attributes.totalSeats ?? 0),
    monthlySpend: Number(attributes.monthly_spend ?? attributes.monthlySpend ?? attributes.spend ?? 0),
    members: membersCount,
    role: normalizedRole,
    createdAt:
      typeof attributes.created_at === 'string'
        ? attributes.created_at
        : (attributes.createdAt as string | undefined),
    lastActive:
      typeof attributes.updated_at === 'string'
        ? attributes.updated_at
        : (attributes.updatedAt as string | undefined),
    pendingInvitations: Number(attributes.pending_invitations ?? attributes.pendingInvitations ?? 0),
    slug: typeof attributes.slug === 'string' ? attributes.slug : (attributes.team_slug as string | undefined),
    memberships: memberships.length ? memberships : undefined,
    membershipsComplete: memberships.length > 0 && (membershipsFromMetaArray || memberships.length === rawMembersCount),
  } as CustomerTeamSummary;
}

function findIncluded<TAttributes = Record<string, unknown>>(response: JsonApiResponse, type: string, id: string): JsonApiResource<TAttributes> | undefined {
  return response.included?.find((item) => item.type === type && item.id === id) as JsonApiResource<TAttributes> | undefined;
}

function readMember(resource: JsonApiResource, response: JsonApiResponse): CustomerTeamMember {
  const attributes = resolveAttributes(resource);
  const memberAttributes = (attributes.member ?? attributes.customer ?? {}) as Record<string, unknown>;

  const membershipRelationship = resolveRelationship(resource, 'membership');
  const relationshipMeta = membershipRelationship?.meta ?? {};
  const resourceRecord = resource as unknown as Record<string, unknown>;
  const resourceMetaRaw = resource.meta ?? (isRecord(resourceRecord._meta) ? (resourceRecord._meta as Record<string, unknown>) : {});
  const resourceMeta = isRecord(resourceMetaRaw) ? resourceMetaRaw : {};
  const relationshipMetaRecord = isRecord(relationshipMeta) ? (relationshipMeta as Record<string, unknown>) : {};
  const meta = { ...resourceMeta, ...relationshipMetaRecord } as Record<string, unknown>;

  const customerRelationship = resolveRelationship(resource, 'customer');
  const customerRefs = normalizeRelationshipRefs(customerRelationship?.data);
  const customerRef = customerRefs[0];
  const customer = customerRef ? findIncluded(response, customerRef.type ?? 'customers', customerRef.id) : undefined;
  const customerAttributes = (customer?.attributes ?? {}) as Record<string, unknown>;

  const roleKey =
    toOptionalString(attributes.role) ??
    toOptionalString(attributes.role_key) ??
    toOptionalString(meta.role) ??
    toOptionalString(meta.role_key) ??
    'member';
  const normalizedRoleKey = roleKey ? roleKey.toLowerCase() : 'member';
  const normalizedRole = ROLE_LABEL_FROM_META[normalizedRoleKey] ?? 'member';

  const roleLabel =
    toOptionalString(attributes.role_label) ??
    toOptionalString(meta.role_label) ??
    toOptionalString(meta.roleLabel) ??
    DEFAULT_ROLE_LABEL[normalizedRole];

  const statusKey =
    toOptionalString(attributes.status) ??
    toOptionalString(meta.status) ??
    'active';
  const normalizedStatusKey = statusKey ? statusKey.toLowerCase() : 'active';
  const normalizedStatus = STATUS_LABEL_FROM_META[normalizedStatusKey] ?? 'active';

  const statusLabel =
    toOptionalString(attributes.status_label) ??
    toOptionalString(meta.status_label) ??
    toOptionalString(meta.statusLabel) ??
    (normalizedStatus === 'active' ? 'Active' : normalizedStatus);

  const joinedAt =
    toOptionalString(attributes.joined_at) ??
    toOptionalString(attributes.joinedAt) ??
    toOptionalString(meta.joined_at) ??
    toOptionalString(meta.joinedAt) ??
    new Date().toISOString();

  const invitedAt =
    toOptionalString(attributes.invited_at) ??
    toOptionalString(attributes.invitedAt) ??
    toOptionalString(meta.invited_at) ??
    toOptionalString(meta.invitedAt);

  const lastActive =
    toOptionalString(memberAttributes.last_active) ??
    toOptionalString(memberAttributes.lastActive) ??
    toOptionalString(customerAttributes.last_active) ??
    toOptionalString(customerAttributes.lastActive) ??
    toOptionalString(meta.last_active) ??
    toOptionalString(meta.lastActive);

  let email = resolveEmail(memberAttributes);
  if (!email) {
    email = resolveEmail(customerAttributes);
  }
  if (!email) {
    email = toOptionalString(meta.member_email) ?? '';
  }

  let name = resolveCustomerName(memberAttributes);
  if (name === 'Unknown member') {
    name = resolveCustomerName(customerAttributes);
  }
  if (name === 'Unknown member') {
    name =
      toOptionalString(meta.member_name) ??
      toOptionalString(meta.memberName) ??
      (email ? email.split('@')[0] : 'Unknown member');
  }

  const avatarUrl = resolveAvatar(memberAttributes) ?? resolveAvatar(customerAttributes);

  const customerId =
    toOptionalString(memberAttributes.id) ??
    (customerRef && typeof customerRef.id === 'string' ? customerRef.id : undefined) ??
    resource.id;

  const creditLimitRaw =
    attributes.credit_limit ??
    attributes.creditLimit ??
    meta.credit_limit ??
    meta.creditLimit ??
    relationshipMetaRecord.credit_limit ??
    relationshipMetaRecord.creditLimit;
  let creditLimit: number | null | undefined;
  if (creditLimitRaw === null) {
    creditLimit = null;
  } else if (creditLimitRaw === undefined) {
    creditLimit = null;
  } else {
    creditLimit = toOptionalNumber(creditLimitRaw) ?? null;
  }

  const creditUsage =
    toOptionalNumber(attributes.credit_usage ?? attributes.creditUsage ?? meta.credit_usage ?? meta.creditUsage) ?? 0;

  const creditRemainingRaw =
    attributes.credit_remaining ??
    attributes.creditRemaining ??
    meta.credit_remaining ??
    meta.creditRemaining;
  const creditRemaining =
    creditRemainingRaw === null || creditRemainingRaw === undefined
      ? undefined
      : toOptionalNumber(creditRemainingRaw);

  return {
    id: resource.id,
    customerId,
    name,
    email,
    role: normalizedRole,
    roleLabel,
    status: normalizedStatus,
    statusLabel,
    joinedAt,
    invitedAt,
    lastActive,
    avatarUrl,
    creditLimit,
    creditUsage,
    creditRemaining,
  };
}

function readInvitation(resource: JsonApiResource, response: JsonApiResponse): CustomerTeamInvitation {
  const attributes = resolveAttributes(resource);

  const invitedByRelationship = resolveRelationship(resource, 'invited_by');
  const invitedByRefs = normalizeRelationshipRefs(invitedByRelationship?.data);
  const invitedByRef = invitedByRefs[0];
  const invitedByResource = invitedByRef ? findIncluded(response, invitedByRef.type ?? 'customers', invitedByRef.id) : undefined;
  const invitedByAttributes = invitedByResource ? resolveAttributes(invitedByResource) : {};
  const invitedByMeta = invitedByRelationship?.meta ?? {};

  const inviterName =
    toOptionalString(invitedByAttributes.name) ??
    toOptionalString(invitedByAttributes.full_name) ??
    toOptionalString(invitedByAttributes.fullName) ??
    (isRecord(invitedByMeta) ? toOptionalString(invitedByMeta.name) ?? toOptionalString(invitedByMeta.full_name) ?? toOptionalString(invitedByMeta.fullName) : undefined);

  const roleKeyRaw = toOptionalString(attributes.role) ?? toOptionalString(attributes.role_key) ?? 'member';
  const normalizedRoleKey = roleKeyRaw ? roleKeyRaw.toLowerCase() : 'member';
  const role = ROLE_LABEL_FROM_META[normalizedRoleKey] ?? 'member';

  const statusKeyRaw = toOptionalString(attributes.status) ?? 'pending';
  const normalizedStatusKey = statusKeyRaw ? statusKeyRaw.toLowerCase() : 'pending';
  const status = normalizedStatusKey as CustomerTeamInvitation['status'];

  const statusLabel =
    toOptionalString(attributes.status_label) ??
    toOptionalString(attributes.statusLabel) ??
    (status === 'pending' ? 'Pending' : status);

  return {
    id: resource.id,
    email: String(attributes.email ?? ''),
    role,
    invitedBy: inviterName ?? 'Team Owner',
    invitedAt:
      toOptionalString(attributes.created_at) ??
      toOptionalString(attributes.createdAt) ??
      toOptionalString(attributes.invited_at) ??
      toOptionalString(attributes.invitedAt) ??
      new Date().toISOString(),
    expiresAt: toOptionalString(attributes.expires_at) ?? toOptionalString(attributes.expiresAt),
    status,
    statusLabel,
    respondedAt: toOptionalString(attributes.responded_at) ?? toOptionalString(attributes.respondedAt),
    message: toOptionalString(attributes.message),
  };
}

class CustomerTeamsService {
  async listTeams(): Promise<CustomerTeamSummary[]> {
    const response = await apiClient.get<JsonApiResponse<JsonApiResource[]>>('/api/v1/teams');
    const resources = Array.isArray(response.data.data)
      ? response.data.data.filter((resource): resource is JsonApiResource => Boolean(resource && typeof resource === 'object'))
      : [];
    return resources.map((resource) => readTeamSummary(resource, response.data));
  }

  async createTeam(input: CreateCustomerTeamInput): Promise<CreateCustomerTeamResult> {
    const body: Record<string, unknown> = {
      name: input.name,
    };

    if (input.description) {
      body.description = input.description;
    }

    if (input.slug) {
      body.slug = input.slug;
    }

    if (typeof input.maxSeats === 'number') {
      body.max_seats = input.maxSeats;
    }

    if (input.settings) {
      body.settings = input.settings;
    }

    if (input.subscriptionId !== undefined) {
      body.subscription_id = input.subscriptionId;
    }

    const response = await apiClient.post<JsonApiResponse<JsonApiResource>, Record<string, unknown>>(
      '/api/v1/teams',
      body,
    );

    const payload = response.data as JsonApiResponse<JsonApiResource> | Record<string, unknown>;

    let resource: JsonApiResource | undefined;
    let included: JsonApiResource[] | undefined;

    if (isRecord(payload) && 'data' in payload && payload.data) {
      resource = payload.data as JsonApiResource;
      included = Array.isArray(payload.included) ? (payload.included as JsonApiResource[]) : undefined;
    } else if (isRecord(payload)) {
      resource = {
        id: String(payload.id ?? ''),
        type: String(payload.type ?? 'teams'),
        attributes: payload,
      };
    }

    if (!resource) {
      throw new Error('Team response payload was not in the expected format.');
    }

    const metaRaw =
      (isRecord(payload) && isRecord(payload.meta) ? payload.meta : undefined) ??
      (isRecord(response.data) && isRecord(response.data.meta) ? response.data.meta : undefined) ??
      (isRecord(response.meta) ? response.meta : undefined);

    const metaRecord = isRecord(metaRaw) ? metaRaw : {};

    const resultMeta = {
      message: toOptionalString(metaRecord.message),
      subscriptionCanceledAtPeriodEnd:
        typeof metaRecord.subscription_canceled_at_period_end === 'boolean'
          ? metaRecord.subscription_canceled_at_period_end
          : (metaRecord.subscriptionCanceledAtPeriodEnd as boolean | undefined),
      canceledSubscriptionCount:
        typeof metaRecord.canceled_subscription_count === 'number'
          ? metaRecord.canceled_subscription_count
          : (metaRecord.canceledSubscriptionCount as number | undefined),
    };

    return {
      team: readTeamSummary(resource, { data: resource, included }),
      meta: resultMeta,
    };
  }

  /**
   * @deprecated Membership data now ships with the `/api/v1/teams` payload. Prefer deriving members from `listTeams()`.
   */
  async getTeamMemberships(teamId: string): Promise<CustomerTeamMember[]> {
    const response = await apiClient.get<JsonApiResponse<JsonApiResource[]>>(`/api/v1/teams/${teamId}/memberships?include=customer`);
    const resources = Array.isArray(response.data.data)
      ? response.data.data.filter((resource): resource is JsonApiResource => Boolean(resource && typeof resource === 'object'))
      : [];
    return resources.map((resource) => readMember(resource, response.data));
  }

  async inviteMember(teamId: string, payload: InviteTeamMemberInput): Promise<CustomerTeamInvitation> {
    const body: Record<string, unknown> = {
      email: payload.email,
      role: payload.role,
    };

    if (payload.language) {
      body.language = payload.language;
    }

    if (payload.message) {
      body.message = payload.message;
    }

    if (typeof payload.expiresInHours === 'number') {
      body.expires_in_hours = payload.expiresInHours;
    }
    const response = await apiClient.post<JsonApiResponse<JsonApiResource>, Record<string, unknown>>(
      `/api/v1/teams/${teamId}/invitations`,
      body
    );

    const payloadResponse = response.data as JsonApiResponse<JsonApiResource> | Record<string, unknown>;

    let resource: JsonApiResource | undefined;
    let included: JsonApiResource[] | undefined;

    if (isRecord(payloadResponse) && 'data' in payloadResponse && payloadResponse.data) {
      resource = payloadResponse.data as JsonApiResource;
      included = Array.isArray(payloadResponse.included)
        ? (payloadResponse.included as JsonApiResource[])
        : undefined;
    } else if (isRecord(payloadResponse)) {
      resource = {
        id: String(payloadResponse.id ?? `${teamId}:${body.email ?? ''}`),
        type: String(payloadResponse.type ?? 'team-invitations'),
        attributes: payloadResponse,
      };
    }

    if (!resource) {
      throw new Error('Invitation response payload was not in the expected format.');
    }

    return readInvitation(resource, { data: resource, included });
  }

  async getTeamInvitations(teamId: string): Promise<CustomerTeamInvitation[]> {
    const response = await apiClient.get<JsonApiResponse<JsonApiResource[]>>(
      `/api/v1/teams/${teamId}/invitations?include=invited_by`
    );
    const resources = Array.isArray(response.data.data)
      ? response.data.data.filter((resource): resource is JsonApiResource => Boolean(resource && typeof resource === 'object'))
      : [];
    return resources.map((resource) => readInvitation(resource, response.data));
  }

  async resendInvitation(teamId: string, invitationId: string, message?: string): Promise<{ message: string }> {
    const response = await apiClient.post<{ meta?: Record<string, unknown> }, { message?: string }>(
      `/api/v1/teams/${teamId}/invitations/${invitationId}/resend`,
      message && message.trim().length ? { message: message.trim() } : {}
    );

    const meta = (response.data.meta ?? {}) as Record<string, unknown>;

    return {
      message: toOptionalString(meta.message) ?? 'Invitation resent successfully',
    };
  }

  async revokeInvitation(teamId: string, invitationId: string, reason?: string): Promise<{ message: string }> {
    const response = await apiClient.delete<{ meta?: Record<string, unknown> }>(
      `/api/v1/teams/${teamId}/invitations/${invitationId}`,
      {
        data: reason && reason.trim().length ? { reason: reason.trim() } : undefined,
      }
    );

    const meta = (response.data.meta ?? {}) as Record<string, unknown>;

    return {
      message: toOptionalString(meta.message) ?? 'Invitation revoked',
    };
  }

  async updateMemberRole(
    teamId: string,
    customerId: string,
    payload: UpdateTeamMemberRoleInput & { creditLimit?: number | null; permissions?: Record<string, unknown> }
  ): Promise<{ message?: string }> {
    const attributes: Record<string, unknown> = {
      role: payload.role,
    };

    if (payload.creditLimit !== undefined) {
      attributes.credit_limit = payload.creditLimit;
    }

    if (payload.permissions !== undefined) {
      attributes.permissions = payload.permissions;
    }

    const requestBody = {
      data: {
        type: 'team-memberships',
        attributes,
      },
    };

    const response = await apiClient.patch<{ meta?: Record<string, unknown> }, typeof requestBody>(
      `/api/v1/teams/${teamId}/memberships/${customerId}`,
      requestBody
    );

    const meta = (response.data.meta ?? {}) as Record<string, unknown>;
    const message = toOptionalString(meta.message);

    return { message };
  }

  async removeMember(teamId: string, customerId: string): Promise<void> {
    await apiClient.delete(`/api/teams/${teamId}/memberships/${customerId}`);
  }

  async getMetrics(): Promise<CustomerTeamStats> {
    const response = await apiClient.get<JsonApiResponse<JsonApiResource>>('/api/v1/team-metrics');
    const attributes = response.data.data.attributes ?? {};
    return {
      totalCustomers: Number(attributes.total_customers ?? attributes.totalCustomers ?? 0),
      teamOwners: Number(attributes.team_owners ?? attributes.teamOwners ?? 0),
      teamMembers: Number(attributes.team_members ?? attributes.teamMembers ?? 0),
      individualOnly: Number(attributes.individual_only ?? attributes.individualOnly ?? 0),
      totalTeams: Number(attributes.total_teams ?? attributes.totalTeams ?? 0),
    };
  }
}

export const customerTeamsService = new CustomerTeamsService();
